import NumberInputComponent from "@/components/inputs/NumberInput";
import useRequest from "@/lib/request/useRequest";
import { DSSATSoil, Field, LossFunctionParameters } from "@/types/field";
import { yupResolver } from "@hookform/resolvers/yup";
import {
    Button,
    Divider,
    Input,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    Tab,
    Tabs,
} from "@heroui/react";
import { useCallback, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { InferType, number, object } from "yup";
import { isNumber } from "@/lib/utils/numbers";

const DSSAT_LAYER_DEPTHS = ["5", "15", "30", "60", "100", "200"] as const;

const DSSAT_LAYER_COLUMNS = [
    "SBDM",
    "SCEC",
    "SDUL",
    "SLLL",
    "SLOC",
    "Adj SLOC",
    "SRGF",
    "SSAT",
] as const;

const ZERO_SOIL_LAYER = {
    SBDM: 0,
    SCEC: 0,
    SDUL: 0,
    SLLL: 0,
    SLOC: 0,
    SRGF: 0,
    SSAT: 0,
} as const;

function organicMatterToOrganicCarbon(organicMatterPercent: number): number {
    return organicMatterPercent * 0.55;
}

type DssatLayers = DSSATSoil["LAYERS"];

function displaySlocsAfterFieldOrganicMatter(
    layers: DssatLayers,
    organicMatterPercent: number,
): Record<(typeof DSSAT_LAYER_DEPTHS)[number], number | null> {
    const sortedDepths = [...DSSAT_LAYER_DEPTHS].sort(
        (a, b) => Number(a) - Number(b),
    );
    const topDepth = sortedDepths[0];
    const topLayerSlocRaw =
        topDepth != null ? layers[topDepth]?.SLOC : undefined;
    const topLayerSloc =
        typeof topLayerSlocRaw === "number" && Number.isFinite(topLayerSlocRaw)
            ? topLayerSlocRaw
            : null;

    const scaleLayerSloc = (sloc: number | null): number | null => {
        if (sloc == null) return null;
        if (
            topLayerSloc == null ||
            topLayerSloc <= 0 ||
            organicMatterPercent == null ||
            !Number.isFinite(organicMatterPercent)
        ) {
            return sloc;
        }
        const targetSurfaceSloc =
            organicMatterToOrganicCarbon(organicMatterPercent);
        if (targetSurfaceSloc <= 0 || targetSurfaceSloc === topLayerSloc) {
            return sloc;
        }
        const scaleFactor = targetSurfaceSloc / topLayerSloc;
        return Math.round(sloc * scaleFactor * 1000) / 1000;
    };

    return Object.fromEntries(
        DSSAT_LAYER_DEPTHS.map((depth) => {
            const raw = layers[depth]?.SLOC;
            const sloc = isNumber(raw) ? Number(raw) : null;
            return [depth, scaleLayerSloc(sloc)];
        }),
    ) as Record<(typeof DSSAT_LAYER_DEPTHS)[number], number | null>;
}

const soilLayerSchema = object({
    SBDM: number().typeError("Required").required("Required"),
    SCEC: number().typeError("Required").required("Required"),
    SDUL: number().typeError("Required").required("Required"),
    SLLL: number().typeError("Required").required("Required"),
    SLOC: number().typeError("Required").required("Required"),
    SRGF: number().typeError("Required").required("Required"),
    SSAT: number().typeError("Required").required("Required"),
});

const dssatSoilFormSchema = object({
    ALBEDO: number().typeError("Required").required("Required"),
    LAYERS: object({
        "5": soilLayerSchema,
        "15": soilLayerSchema,
        "30": soilLayerSchema,
        "60": soilLayerSchema,
        "100": soilLayerSchema,
        "200": soilLayerSchema,
    }).required(),
    DRAINAGE_RATE: number().typeError("Required").required("Required"),
    RUNOFF_CURVE_NO: number().typeError("Required").required("Required"),
    FERTILITY_FACTOR: number().typeError("Required").required("Required"),
    S1_EVAPORATION_LIMIT: number().typeError("Required").required("Required"),
    MINERALIZATION_FACTOR: number().typeError("Required").required("Required"),
});

const lossFunctionFormSchema = object({
    w1: number().typeError("Required").required("Required"),
    w2: number().typeError("Required").required("Required"),
    w3: number().typeError("Required").required("Required"),
    w4: number().typeError("Required").required("Required"),
    w5: number().typeError("Required").required("Required"),
    n_cost_per_lb: number().typeError("Required").required("Required"),
    app_cost: number().typeError("Required").required("Required"),
    crop_price: number().typeError("Required").required("Required"),
    bu_per_n: number().typeError("Required").required("Required"),
    niad_thresh: number().typeError("Required").required("Required"),
    remob_thresh: number().typeError("Required").required("Required"),
    roi_safe_frac: number().typeError("Required").required("Required"),
    roi_gap_power: number().typeError("Required").required("Required"),
});

const schema = object({
    dssat_soil: dssatSoilFormSchema,
    loss_function: lossFunctionFormSchema,
});

type ParameterFormValues = InferType<typeof schema>;

function AdvisorParams({
    onClose,
    fieldId,
}: {
    onClose: () => void;
    fieldId: string;
}) {
    const { useGetRequest, useMutateRequest } = useRequest();

    const fieldQuery = useGetRequest<Field>({
        url: `/fields/${fieldId}/`,
        enabled: !!fieldId,
    });
    const advisorParameters = useGetRequest<{
        dssat_soil: DSSATSoil;
        loss_function_parameters: LossFunctionParameters;
    } | null>({
        url: `fields/${fieldId}/advisor_parameters/`,
        enabled: !!fieldId,
    });

    const advisorParametersMutation = useMutateRequest<{
        dssat_soil: DSSATSoil;
        loss_function_parameters: LossFunctionParameters;
    }>({
        url: `fields/${fieldId}/advisor_parameters/`,
        method: "put",
        successMessage: "DSSAT soil saved",
    });

    const resetMutation = useMutateRequest<DSSATSoil>({
        url: `fields/${fieldId}/fetch_dssat_soil/`,
        method: "put",
    });

    const setSoilToDefault = useCallback(
        (type: "ssurgo" | "soilgrids") => {
            resetMutation.mutate(
                {
                    params: {
                        type,
                    },
                },
                {
                    onSuccess: () => {
                        advisorParameters?.refetch();
                    },
                },
            );
        },
        [resetMutation, advisorParameters],
    );

    const form = useForm<ParameterFormValues>({
        resolver: yupResolver(schema),
        defaultValues: {
            dssat_soil: {
                ALBEDO: 0,
                LAYERS: {
                    "5": { ...ZERO_SOIL_LAYER },
                    "15": { ...ZERO_SOIL_LAYER },
                    "30": { ...ZERO_SOIL_LAYER },
                },
            },
            loss_function: {
                w1: 0,
                w2: 0,
                w3: 0,
                w4: 0,
                w5: 0,
                n_cost_per_lb: 0,
                app_cost: 0,
                crop_price: 0,
                bu_per_n: 0,
                niad_thresh: 0,
                remob_thresh: 0,
                roi_safe_frac: 0,
                roi_gap_power: 0,
            },
        },
    });

    useEffect(() => {
        if (advisorParameters.data) {
            const { dssat_soil, loss_function_parameters } =
                advisorParameters.data;
            form.reset({
                dssat_soil,
                loss_function: loss_function_parameters,
            });
        }
    }, [advisorParameters.data, form]);

    const onSubmit = useCallback(
        (values: ParameterFormValues) => {
            advisorParametersMutation.mutate(
                {
                    data: {
                        dssat_soil: values.dssat_soil,
                        loss_function_parameters: values.loss_function,
                    },
                },
                {
                    onSuccess: () => {
                        advisorParameters.refetch();
                        onClose();
                    },
                },
            );
        },
        [advisorParametersMutation, advisorParameters, onClose],
    );

    const [layers] = form.watch(["dssat_soil.LAYERS"]);

    const [soilOrganicMatter, setSoilOrganicMatter] = useState<number | null>(
        null,
    );
    useEffect(() => {
        if (fieldQuery.data?.soil_organic_matter) {
            setSoilOrganicMatter(fieldQuery.data.soil_organic_matter);
        }
    }, [fieldQuery.data?.soil_organic_matter]);

    // const displaySlocByDepth = useMemo(() => {
    //     if (!soilOrganicMatter) return null;
    //     return displaySlocsAfterFieldOrganicMatter(layers, soilOrganicMatter);
    // }, [soilOrganicMatter, layers]);

    const displaySlocByDepth = soilOrganicMatter
        ? displaySlocsAfterFieldOrganicMatter(layers, soilOrganicMatter)
        : null;

    const fieldMutation = useMutateRequest<Field>({
        url: `fields/bulk_update/`,
        method: "patch",
        successMessage: "Soil organic matter saved",
        errorMessage: "Failed to save soil organic matter",
    });

    const saveSoilOrganicMatter = useCallback(() => {
        fieldMutation.mutate({
            data: [
                {
                    id: fieldId,
                    soil_organic_matter: soilOrganicMatter,
                },
            ],
        });
    }, [fieldId, fieldMutation, soilOrganicMatter]);

    return (
        <Modal isOpen size="3xl" onClose={onClose} isDismissable={false}>
            <ModalContent className="min-h-[80vh]">
                <FormProvider {...form}>
                    <form
                        className="flex flex-col"
                        onSubmit={form.handleSubmit(onSubmit)}
                    >
                        <ModalHeader className="flex flex-row justify-between items-center pr-10">
                            <div className="text-lg font-bold">
                                Advisor Parameters
                            </div>
                        </ModalHeader>
                        <ModalBody>
                            <Tabs>
                                <Tab
                                    title="Soil Organic Matter"
                                    className="flex flex-col gap-2"
                                >
                                    <div className="flex flex-row gap-3 items-end justify-between">
                                        <div className="flex flex-row gap-3 items-end">
                                            <Input
                                                type="number"
                                                label="Soil Organic Matter (%)"
                                                size="sm"
                                                step="any"
                                                min={0}
                                                labelPlacement="outside"
                                                placeholder=" "
                                                max={100}
                                                value={
                                                    soilOrganicMatter?.toString() ??
                                                    undefined
                                                }
                                                onChange={(e) =>
                                                    setSoilOrganicMatter(
                                                        Number(e.target.value),
                                                    )
                                                }
                                            />
                                            <div>
                                                <Button
                                                    color="primary"
                                                    size="sm"
                                                    onPress={() =>
                                                        saveSoilOrganicMatter()
                                                    }
                                                    isLoading={
                                                        fieldMutation.isPending
                                                    }
                                                >
                                                    Save OM
                                                </Button>
                                            </div>
                                        </div>
                                        <div className="flex flex-row gap-2">
                                            <Button
                                                color="primary"
                                                size="sm"
                                                variant="light"
                                                onPress={() =>
                                                    setSoilToDefault("ssurgo")
                                                }
                                                isLoading={
                                                    resetMutation.isPending
                                                }
                                            >
                                                Reset all to SSURGO
                                            </Button>
                                            <Button
                                                color="primary"
                                                size="sm"
                                                variant="light"
                                                onPress={() =>
                                                    setSoilToDefault(
                                                        "soilgrids",
                                                    )
                                                }
                                                isLoading={
                                                    resetMutation.isPending
                                                }
                                            >
                                                Reset all to Soilgrids
                                            </Button>
                                        </div>
                                    </div>
                                    <div className="text-xs text-default-500">
                                        Soil Organic Matter will be used to
                                        scale the SLOC to match this value - see
                                        Adj SLOC
                                    </div>
                                    <div className="grid grid-cols-2 gap-1 md:grid-cols-3 ">
                                        <NumberInputComponent
                                            label="ALBEDO"
                                            name="dssat_soil.ALBEDO"
                                            size="sm"
                                            step="any"
                                            labelPlacement="outside"
                                        />
                                        <NumberInputComponent
                                            label="DRAINAGE_RATE"
                                            name="dssat_soil.DRAINAGE_RATE"
                                            size="sm"
                                            step="any"
                                            labelPlacement="outside"
                                        />
                                        <NumberInputComponent
                                            label="RUNOFF_CURVE_NO"
                                            name="dssat_soil.RUNOFF_CURVE_NO"
                                            size="sm"
                                            step="any"
                                            labelPlacement="outside"
                                        />
                                        <NumberInputComponent
                                            label="FERTILITY_FACTOR"
                                            name="dssat_soil.FERTILITY_FACTOR"
                                            size="sm"
                                            step="any"
                                            labelPlacement="outside"
                                        />
                                        <NumberInputComponent
                                            label="S1_EVAPORATION_LIMIT"
                                            name="dssat_soil.S1_EVAPORATION_LIMIT"
                                            size="sm"
                                            step="any"
                                            labelPlacement="outside"
                                        />
                                        <NumberInputComponent
                                            label="MINERALIZATION_FACTOR"
                                            name="dssat_soil.MINERALIZATION_FACTOR"
                                            size="sm"
                                            step="any"
                                            labelPlacement="outside"
                                        />
                                    </div>

                                    <Divider />

                                    <div className="overflow-x-auto">
                                        <div
                                            className="grid min-w-[36rem] gap-x-2 gap-y-2"
                                            style={{
                                                gridTemplateColumns:
                                                    "minmax(4.5rem, auto) repeat(8, minmax(0, 1fr))",
                                            }}
                                        >
                                            <div className="text-small font-semibold text-default-500">
                                                Depth (cm)
                                            </div>
                                            {DSSAT_LAYER_COLUMNS.map(
                                                (field) => (
                                                    <div
                                                        key={field}
                                                        className="text-small font-semibold text-center"
                                                    >
                                                        {field}
                                                    </div>
                                                ),
                                            )}
                                            {DSSAT_LAYER_DEPTHS.map((depth) => (
                                                <div
                                                    key={depth}
                                                    className="contents"
                                                >
                                                    <div className="text-small font-medium flex items-center">
                                                        {depth} cm
                                                    </div>
                                                    {DSSAT_LAYER_COLUMNS.map(
                                                        (field) => {
                                                            if (
                                                                field ===
                                                                "Adj SLOC"
                                                            ) {
                                                                const sloc =
                                                                    displaySlocByDepth?.[
                                                                        depth
                                                                    ];
                                                                const actualSloc =
                                                                    isNumber(
                                                                        layers?.[
                                                                            depth
                                                                        ]?.SLOC,
                                                                    )
                                                                        ? Number(
                                                                              layers?.[
                                                                                  depth
                                                                              ]
                                                                                  ?.SLOC,
                                                                          )
                                                                        : null;

                                                                return (
                                                                    <div
                                                                        key={`${depth}-${field}`}
                                                                        className="text-sm flex items-center "
                                                                    >
                                                                        {sloc
                                                                            ? sloc.toFixed(
                                                                                  3,
                                                                              )
                                                                            : actualSloc?.toFixed(
                                                                                  3,
                                                                              )}
                                                                    </div>
                                                                );
                                                            }
                                                            return (
                                                                <NumberInputComponent
                                                                    key={`${depth}-${field}`}
                                                                    name={`dssat_soil.LAYERS.${depth}.${field}`}
                                                                    size="sm"
                                                                />
                                                            );
                                                        },
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </Tab>
                                <Tab title="Loss Function">
                                    <div className="flex flex-col gap-4">
                                        <div className="grid grid-cols-5 gap-3">
                                            <NumberInputComponent
                                                label="w1"
                                                name="loss_function.w1"
                                                size="sm"
                                                step="any"
                                                labelPlacement="outside"
                                                description="pen_early_cwad_by_rate"
                                            />
                                            <NumberInputComponent
                                                label="w2"
                                                name="loss_function.w2"
                                                size="sm"
                                                step="any"
                                                labelPlacement="outside"
                                                description="pen_niad_by_rate"
                                            />
                                            <NumberInputComponent
                                                label="w3"
                                                name="loss_function.w3"
                                                size="sm"
                                                step="any"
                                                labelPlacement="outside"
                                                description="pen_remob_by_rate"
                                            />
                                            <NumberInputComponent
                                                label="w4"
                                                name="loss_function.w4"
                                                size="sm"
                                                step="any"
                                                labelPlacement="outside"
                                                description="roi_pen_by_rate"
                                            />
                                            <NumberInputComponent
                                                label="w5"
                                                name="loss_function.w5"
                                                size="sm"
                                                step="any"
                                                labelPlacement="outside"
                                                description="pen_early_nupc_by_rate"
                                            />
                                        </div>
                                        <div className="grid grid-cols-4 gap-3">
                                            <NumberInputComponent
                                                label="n_cost_per_lb"
                                                name="loss_function.n_cost_per_lb"
                                                size="sm"
                                                step="any"
                                                labelPlacement="outside"
                                            />
                                            <NumberInputComponent
                                                label="app_cost"
                                                name="loss_function.app_cost"
                                                size="sm"
                                                step="any"
                                                labelPlacement="outside"
                                            />
                                            <NumberInputComponent
                                                label="crop_price"
                                                name="loss_function.crop_price"
                                                size="sm"
                                                step="any"
                                                labelPlacement="outside"
                                            />
                                            <NumberInputComponent
                                                label="bu_per_n"
                                                name="loss_function.bu_per_n"
                                                size="sm"
                                                step="any"
                                                labelPlacement="outside"
                                            />
                                            <NumberInputComponent
                                                label="niad_thresh"
                                                name="loss_function.niad_thresh"
                                                size="sm"
                                                step="any"
                                                labelPlacement="outside"
                                            />
                                            <NumberInputComponent
                                                label="remob_thresh"
                                                name="loss_function.remob_thresh"
                                                size="sm"
                                                step="any"
                                                labelPlacement="outside"
                                            />
                                            <NumberInputComponent
                                                label="roi_safe_frac"
                                                name="loss_function.roi_safe_frac"
                                                size="sm"
                                                step="any"
                                                labelPlacement="outside"
                                            />
                                            <NumberInputComponent
                                                label="roi_gap_power"
                                                name="loss_function.roi_gap_power"
                                                size="sm"
                                                step="any"
                                                labelPlacement="outside"
                                            />
                                        </div>
                                    </div>
                                </Tab>
                            </Tabs>
                        </ModalBody>
                        <ModalFooter>
                            <Button
                                variant="light"
                                onPress={onClose}
                                isDisabled={advisorParametersMutation.isPending}
                            >
                                Cancel
                            </Button>
                            <Button
                                color="primary"
                                type="submit"
                                isLoading={advisorParametersMutation.isPending}
                                isDisabled={advisorParametersMutation.isPending}
                            >
                                Save
                            </Button>
                        </ModalFooter>
                    </form>
                </FormProvider>
            </ModalContent>
        </Modal>
    );
}

export default AdvisorParams;
